/**
 * MatchTermsTab — Match terms & virtual devices with sub-tab toggle.
 *
 * Sub-tabs:
 *   "Terms" — Match term table with add/edit/delete
 *   "Virtual Devices" — Virtual device table with add/delete
 *
 * Both views support checkbox selection for bulk delete.
 */

import { useState, useMemo, useCallback } from 'react';
import usePersistedPerPage from '../../../../hooks/usePersistedPerPage.js';
import Pagination from '../../../../components/v2/Pagination.jsx';
import { ariaSort } from '../../../../components/v2/SortableTh.jsx';

/** Match type labels */
const MATCH_TYPES = {
  1: 'Create', 2: 'Repeat', 3: 'Clear', 4: 'Ignore',
  5: 'Change', 6: 'Area', 7: 'Device',
};

const MatchTermsTab = ({
  matchTermData, virtualDevices, virtualDeviceTypes,
  editRights, onAddMatchTerm, onEditMatchTerm,
  onDeleteMatchTerms, onAddVirtualDevice, onDeleteVirtualDevices,
}) => {
  const [subTab, setSubTab] = useState('terms');
  const [selectedTerms, setSelectedTerms] = useState([]);
  const [selectedDevices, setSelectedDevices] = useState([]);

  // Search
  const [termSearch, setTermSearch] = useState('');
  const [deviceSearch, setDeviceSearch] = useState('');

  // Sort
  const [termSortCol, setTermSortCol] = useState('pattern');
  const [termSortDir, setTermSortDir] = useState('asc');

  // Pagination — the shared Pagination component, as in DatabaseTab. perPage is
  // shared across both sub-tables;
  // sub-tabs share a single visual control so the user only sets it once.
  const [termPage, setTermPage] = useState(1);
  const [devicePage, setDevicePage] = useState(1);
  const [perPage, setPerPage] = usePersistedPerPage();  // shared, persisted choice

  // --- TERMS ---

  const filteredTerms = useMemo(() => {
    let rows = [...(matchTermData || [])];
    if (termSearch) {
      const q = termSearch.toLowerCase();
      rows = rows.filter((r) =>
        (r.pattern || '').toLowerCase().includes(q) ||
        (r.replacement || '').toLowerCase().includes(q) ||
        (MATCH_TYPES[r.match_type_id] || '').toLowerCase().includes(q)
      );
    }
    rows.sort((a, b) => {
      const aVal = (a[termSortCol] || '').toString().toLowerCase();
      const bVal = (b[termSortCol] || '').toString().toLowerCase();
      if (aVal < bVal) return termSortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return termSortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return rows;
  }, [matchTermData, termSearch, termSortCol, termSortDir]);

  const termTotalPages = Math.max(1, Math.ceil(filteredTerms.length / perPage));
  const termPageRows = filteredTerms.slice((termPage - 1) * perPage, termPage * perPage);

  const handleTermSort = useCallback((col) => {
    if (termSortCol === col) {
      setTermSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setTermSortCol(col);
      setTermSortDir('asc');
    }
    setTermPage(1);
  }, [termSortCol]);

  const toggleTermSelection = useCallback((item) => {
    setSelectedTerms((prev) =>
      prev.some((s) => s.matchterm_id === item.matchterm_id)
        ? prev.filter((s) => s.matchterm_id !== item.matchterm_id)
        : [...prev, item]
    );
  }, []);

  const toggleAllTerms = useCallback(() => {
    if (selectedTerms.length === termPageRows.length) {
      setSelectedTerms([]);
    } else {
      setSelectedTerms([...termPageRows]);
    }
  }, [selectedTerms, termPageRows]);

  // --- VIRTUAL DEVICES ---

  const filteredDevices = useMemo(() => {
    let rows = [...(virtualDevices || [])];
    if (deviceSearch) {
      const q = deviceSearch.toLowerCase();
      rows = rows.filter((r) =>
        (r.device_name || '').toLowerCase().includes(q) ||
        (r.device_type_name || '').toLowerCase().includes(q)
      );
    }
    return rows;
  }, [virtualDevices, deviceSearch]);

  const deviceTotalPages = Math.max(1, Math.ceil(filteredDevices.length / perPage));
  const devicePageRows = filteredDevices.slice((devicePage - 1) * perPage, devicePage * perPage);

  const toggleDeviceSelection = useCallback((item) => {
    setSelectedDevices((prev) =>
      prev.some((s) => s.device_id === item.device_id)
        ? prev.filter((s) => s.device_id !== item.device_id)
        : [...prev, item]
    );
  }, []);

  const toggleAllDevices = useCallback(() => {
    if (selectedDevices.length === devicePageRows.length) {
      setSelectedDevices([]);
    } else {
      setSelectedDevices([...devicePageRows]);
    }
  }, [selectedDevices, devicePageRows]);

  const TermSortIcon = ({ col }) => {
    if (termSortCol !== col) return <span className="dp-sort-icon dp-sort-icon--neutral dp-icon-xs">↕</span>;
    return <span className="dp-sort-icon dp-sort-icon--active dp-icon-xs">{termSortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  return (
    <>
      {/* Sub-tab toggle */}
      <div className="pp-subtab-toggle">
        <button
          className={`pp-subtab-btn${subTab === 'terms' ? ' is-active' : ''}`}
          onClick={() => { setSubTab('terms'); setSelectedTerms([]); }}
        >
          Match Terms
        </button>
        <button
          className={`pp-subtab-btn${subTab === 'devices' ? ' is-active' : ''}`}
          onClick={() => { setSubTab('devices'); setSelectedDevices([]); }}
        >
          Virtual Devices
        </button>
      </div>

      {/* === TERMS VIEW === */}
      {subTab === 'terms' && (
        <div className="pp-section">
          <div className="pp-section__header">
            <h3 className="pp-section__title">Match Terms ({filteredTerms.length})</h3>
            <div className="pp-section__actions">
              {editRights && selectedTerms.length > 0 && (
                <button
                  className="dp-btn dp-btn--danger dp-btn--sm"
                  onClick={() => onDeleteMatchTerms(selectedTerms)}
                >
                  Delete ({selectedTerms.length})
                </button>
              )}
              {editRights && (
                <button className="dp-btn dp-btn--primary dp-btn--sm" onClick={onAddMatchTerm}>
                  + Add Term
                </button>
              )}
            </div>
          </div>

          <div className="pp-section__body">
            {/* Search */}
            <div className="dp-controls" style={{ marginBottom: 'var(--space-3)' }}>
              <div className="dp-controls__left">
                <div className="dp-search">
                  <svg className="dp-icon-md dp-search__icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" /></svg>
                  <input
                    className="dp-search__input"
                    placeholder="Search terms…"
                    value={termSearch}
                    onChange={(e) => { setTermSearch(e.target.value); setTermPage(1); }}
                  />
                </div>
              </div>
            </div>

            {filteredTerms.length === 0 ? (
              <div className="dp-empty" style={{ padding: 'var(--space-8) var(--space-4)' }}>
                <p className="dp-empty__title">No match terms</p>
                <p className="dp-empty__text">Add match terms to configure alarm processing rules.</p>
              </div>
            ) : (
              <>
                <div className="dp-table-wrap">
                  <table className="dp-table">
                    <thead className="dp-thead">
                      <tr>
                        {editRights && (
                          <th className="dp-th dp-th--checkbox">
                            <input
                              type="checkbox"
                              className="dp-checkbox"
                              checked={selectedTerms.length === termPageRows.length && termPageRows.length > 0}
                              onChange={toggleAllTerms}
                            />
                          </th>
                        )}
                        <th className="dp-th" aria-sort={ariaSort(termSortCol, termSortDir, 'match_type_id')}>
                          <button className="dp-th__sort-btn" onClick={() => handleTermSort('match_type_id')}>
                            Type <TermSortIcon col="match_type_id" />
                          </button>
                        </th>
                        <th className="dp-th" aria-sort={ariaSort(termSortCol, termSortDir, 'pattern')}>
                          <button className="dp-th__sort-btn" onClick={() => handleTermSort('pattern')}>
                            Pattern <TermSortIcon col="pattern" />
                          </button>
                        </th>
                        <th className="dp-th">Replacement</th>
                        <th className="dp-th">Case Sensitive</th>
                        <th className="dp-th">Area</th>
                        <th className="dp-th">Device</th>
                        <th className="dp-th">Category</th>
                        <th className="dp-th">Event Type</th>
                        <th className="dp-th">TTL</th>
                      </tr>
                    </thead>
                    <tbody>
                      {termPageRows.map((row) => {
                        const isSelected = selectedTerms.some((s) => s.matchterm_id === row.matchterm_id);
                        return (
                          <tr
                            key={row.matchterm_id}
                            className={`dp-tr pp-clickable-row${isSelected ? ' is-selected' : ''}`}
                            onClick={() => editRights && onEditMatchTerm(row)}
                          >
                            {editRights && (
                              <td className="dp-td dp-td--checkbox" onClick={(e) => e.stopPropagation()}>
                                <input
                                  type="checkbox"
                                  className="dp-checkbox"
                                  checked={isSelected}
                                  onChange={() => toggleTermSelection(row)}
                                />
                              </td>
                            )}
                            <td className="dp-td"><span className="dp-cell-text">{MATCH_TYPES[row.match_type_id] || row.match_type_id}</span></td>
                            <td className="dp-td"><span className="dp-cell-text">{row.pattern || '—'}</span></td>
                            <td className="dp-td"><span className="dp-cell-text">{row.replacement || '—'}</span></td>
                            <td className="dp-td"><span className="dp-cell-text">{row.is_case_sensitive ? 'Yes' : 'No'}</span></td>
                            <td className="dp-td"><span className="dp-cell-text">{row.area_name || '—'}</span></td>
                            <td className="dp-td"><span className="dp-cell-text">{row.device_name || '—'}</span></td>
                            <td className="dp-td"><span className="dp-cell-text">{row.event_category_name || '—'}</span></td>
                            <td className="dp-td"><span className="dp-cell-text">{row.event_type_name || '—'}</span></td>
                            <td className="dp-td"><span className="dp-cell-text">{row.ttl ? Math.round(row.ttl / 60) + ' min' : '—'}</span></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {filteredTerms.length > 0 && (
                  <Pagination
                    page={termPage}
                    totalPages={termTotalPages}
                    total={filteredTerms.length}
                    perPage={perPage}
                    onPageChange={setTermPage}
                    onPerPageChange={(n) => { setPerPage(n); setTermPage(1); setDevicePage(1); }}
                    entityLabel={filteredTerms.length === 1 ? 'term' : 'terms'}
                    selectId="match-terms-per-page"
                  />
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* === VIRTUAL DEVICES VIEW === */}
      {subTab === 'devices' && (
        <div className="pp-section">
          <div className="pp-section__header">
            <h3 className="pp-section__title">Virtual Devices ({filteredDevices.length})</h3>
            <div className="pp-section__actions">
              {editRights && selectedDevices.length > 0 && (
                <button
                  className="dp-btn dp-btn--danger dp-btn--sm"
                  onClick={() => onDeleteVirtualDevices(selectedDevices)}
                >
                  Delete ({selectedDevices.length})
                </button>
              )}
              {editRights && (
                <button className="dp-btn dp-btn--primary dp-btn--sm" onClick={onAddVirtualDevice}>
                  + Add Device
                </button>
              )}
            </div>
          </div>

          <div className="pp-section__body">
            {/* Search */}
            <div className="dp-controls" style={{ marginBottom: 'var(--space-3)' }}>
              <div className="dp-controls__left">
                <div className="dp-search">
                  <svg className="dp-icon-md dp-search__icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" /></svg>
                  <input
                    className="dp-search__input"
                    placeholder="Search devices…"
                    value={deviceSearch}
                    onChange={(e) => { setDeviceSearch(e.target.value); setDevicePage(1); }}
                  />
                </div>
              </div>
            </div>

            {filteredDevices.length === 0 ? (
              <div className="dp-empty" style={{ padding: 'var(--space-8) var(--space-4)' }}>
                <p className="dp-empty__title">No virtual devices</p>
                <p className="dp-empty__text">Add virtual devices for match term device assignments.</p>
              </div>
            ) : (
              <>
                <div className="dp-table-wrap">
                  <table className="dp-table">
                    <thead className="dp-thead">
                      <tr>
                        {editRights && (
                          <th className="dp-th dp-th--checkbox">
                            <input
                              type="checkbox"
                              className="dp-checkbox"
                              checked={selectedDevices.length === devicePageRows.length && devicePageRows.length > 0}
                              onChange={toggleAllDevices}
                            />
                          </th>
                        )}
                        <th className="dp-th">Device Name</th>
                        <th className="dp-th">Device Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {devicePageRows.map((row) => {
                        const isSelected = selectedDevices.some((s) => s.device_id === row.device_id);
                        return (
                          <tr key={row.device_id} className={`dp-tr${isSelected ? ' is-selected' : ''}`}>
                            {editRights && (
                              <td className="dp-td dp-td--checkbox">
                                <input
                                  type="checkbox"
                                  className="dp-checkbox"
                                  checked={isSelected}
                                  onChange={() => toggleDeviceSelection(row)}
                                />
                              </td>
                            )}
                            <td className="dp-td"><span className="dp-cell-text">{row.device_name || '—'}</span></td>
                            <td className="dp-td"><span className="dp-cell-text">{row.device_type_name || '—'}</span></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {filteredDevices.length > 0 && (
                  <Pagination
                    page={devicePage}
                    totalPages={deviceTotalPages}
                    total={filteredDevices.length}
                    perPage={perPage}
                    onPageChange={setDevicePage}
                    onPerPageChange={(n) => { setPerPage(n); setTermPage(1); setDevicePage(1); }}
                    entityLabel={filteredDevices.length === 1 ? 'device' : 'devices'}
                    selectId="match-term-devices-per-page"
                  />
                )}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default MatchTermsTab;
